import React, { lazy, Suspense } from 'react'
import { css } from '@emotion/core'
import { AuthRoute, NotFound, Route, Switch, Redirect, useLocation } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import { useFeatureFlag } from '@fs/zion-flags'
import ReportsSkeleton from './pages/reports/ReportsSkeleton'
import OverviewSkeleton from './pages/overview/Skeleton'
import { useAppProvider } from './providers/AppProvider'
import useIsInmate from './hooks/useIsInmate'
import ReportsProvider from './pages/reports/ReportsProvider'
import HintsRoute from './components/auth/HintsRoute'
import ReportsAuth from './components/auth/ReportsAuth'
import PrisonReportsAuth from './components/auth/PrisonReportsAuth'
import SimpleAuthRoute from './components/SimpleAuthRoute'
import LoadingSpinner from './components/LoadingSpinner'

const AdminConsole = lazy(() => import('./pages/admin'))
const CensusPage = lazy(() => import('./components/CensusPage'))
const Corrections = lazy(() => import('./pages/corrections/Corrections'))
const EventsPage = lazy(() => import('./pages/events'))
const EventsManager = lazy(() => import('./pages/events/manager'))
const EventDetail = lazy(() => import('./pages/events/manager/EventDetail'))
const IndexingOverviewPage = lazy(() => import('./pages/indexing-overview/IndexingOverviewPage/IndexingOverviewPage'))
const LabsPage = lazy(() => import('./pages/labs'))
const MobileDeepLink = lazy(() => import('./components/MobileDeepLink'))
const MobileLinkLanding = lazy(() => import('./pages/mobile-link'))
const Opportunities = lazy(() => import('./pages/opportunities'))
const Overview = lazy(() => import('./pages/overview'))
const QuickNameReview = lazy(() => import('./pages/quick-name-review'))
const QnrRefactor = lazy(() => import('./pages/qnr-refactor'))
const RecordHints = lazy(() => import('./pages/record-hints/index'))
const Reports = lazy(() => import('./pages/reports'))
const Share = lazy(() => import('./pages/share'))
const Unauthorized = lazy(() => import('./pages/unauthorized'))
const VerifyPlaces = lazy(() => import('./pages/verify-places'))
const YourImpact = lazy(() => import('./pages/your-impact'))
const FullNameReview = lazy(() => import('./pages/full-name-review'))

const Routes = ({ hasCensusPermission, hasAdminAccess, adminLoading, adminPermissions }) => {
  const { pathname } = useLocation()
  const { userLoading } = useUser()
  const seoIndexingOverviewFlag = useFeatureFlag('platform_SEO_indexingOverview')
  const giveMobileLinkFlag = useFeatureFlag('give_mobile_link')
  const qnrRefactorFlag = useFeatureFlag('give_qnr_refactor')
  const { accessFromPrison } = useAppProvider()
  const { isInmate, isLoading } = useIsInmate()

  const isReady = !isLoading && !userLoading

  if (!isReady) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
        `}
      >
        <LoadingSpinner />
      </div>
    )
  }

  if ((isInmate || accessFromPrison) && !pathname.includes('/corrections')) {
    return <Redirect to="/corrections" />
  }

  return (
    <Switch>
      <Redirect exact from="/overview" to="/" />
      <Route exact path="/">
        <Suspense fallback={<OverviewSkeleton />}>
          <Overview />
        </Suspense>
      </Route>
      <Route path="/indexing">
        <Suspense fallback={null}>
          <IndexingOverviewPage />
        </Suspense>
      </Route>
      <Route exact path="/appstores" component={MobileDeepLink} />
      {giveMobileLinkFlag?.isOn && (
        <Route exact path="/m-link">
          <Suspense fallback={null}>
            <MobileLinkLanding />
          </Suspense>
        </Route>
      )}
      {seoIndexingOverviewFlag?.isOn && seoIndexingOverviewFlag?.config?.indexingOverviewPage && (
        <Route exact path="/seo-indexing/">
          <Suspense fallback={null}>
            <IndexingOverviewPage />
          </Suspense>
        </Route>
      )}
      <Redirect exact from="/my-opportunities" to="/opportunities" />
      <Route exact path="/share/:slug">
        <Suspense fallback={null}>
          <Share />
        </Suspense>
      </Route>
      <HintsRoute path="/record-hints/:ark">
        {(routeProps) => (
          <Suspense fallback={null}>
            <RecordHints {...routeProps} />
          </Suspense>
        )}
      </HintsRoute>
      <SimpleAuthRoute exact path="/quick-name-review">
        {(routeProps) => (
          <Suspense fallback={null}>
            {qnrRefactorFlag?.isOn ? (
              <QnrRefactor data-testid="quick-name-review" {...routeProps} />
            ) : (
              <QuickNameReview data-testid="quick-name-review" {...routeProps} />
            )}
          </Suspense>
        )}
      </SimpleAuthRoute>
      <SimpleAuthRoute exact path="/full-name-review">
        {(routeProps) => (
          <Suspense fallback={null}>
            <FullNameReview data-testid="full-name-review" {...routeProps} />
          </Suspense>
        )}
      </SimpleAuthRoute>
      <Route exact path="/opportunities">
        <Suspense fallback={null}>
          <Opportunities />
        </Suspense>
      </Route>
      <AuthRoute exact path="/your-impact">
        <Suspense fallback={null}>
          <YourImpact data-testid="your-impact" />
        </Suspense>
      </AuthRoute>
      {hasCensusPermission && (
        <AuthRoute exact path="/1950">
          <Suspense fallback={null}>
            <CensusPage />
          </Suspense>
        </AuthRoute>
      )}
      <ReportsAuth exact path="/reports">
        <Suspense fallback={<ReportsSkeleton />}>
          <ReportsProvider>
            <Reports />
          </ReportsProvider>
        </Suspense>
      </ReportsAuth>
      <PrisonReportsAuth path="/prison-reports">
        <Suspense fallback={ReportsSkeleton}>
          <ReportsProvider>
            <Reports showPrisonReports />
          </ReportsProvider>
        </Suspense>
      </PrisonReportsAuth>
      <AuthRoute exact path="/verify-places">
        <VerifyPlaces />
      </AuthRoute>
      {(hasAdminAccess || adminLoading) && (
        <AuthRoute path="/admin">
          <Suspense fallback={null}>
            {adminLoading ? <LoadingSpinner /> : <AdminConsole permissions={adminPermissions} />}
          </Suspense>
        </AuthRoute>
      )}
      <AuthRoute path="/labs">
        <Suspense fallback={null}>
          <LabsPage />
        </Suspense>
      </AuthRoute>
      <Route path="/corrections">
        <Suspense fallback={null}>
          <Corrections />
        </Suspense>
      </Route>
      <Route
        path="/events/:eventId/user"
        render={(routeProps) => (
          <Suspense fallback={null}>
            <EventsPage {...routeProps} />
          </Suspense>
        )}
      />
      <Route
        path="/events/:eventId/dashboard"
        render={(routeProps) => (
          <Suspense fallback={null}>
            <EventsPage {...routeProps} />
          </Suspense>
        )}
      />
      <AuthRoute exact path="/events/manager/:eventId">
        <Suspense fallback={null}>
          <EventDetail />
        </Suspense>
      </AuthRoute>
      <AuthRoute exact path="/events/manager">
        <Suspense fallback={null}>
          <EventsManager />
        </Suspense>
      </AuthRoute>
      <Route path="/unauthorized" component={Unauthorized} />
      <Route component={NotFound} />
    </Switch>
  )
}

export default Routes
